import { useState } from "react";
import { toast } from "sonner";
import { Pulse, Pencil, Check, X, ArrowCounterClockwise, Info, EyeSlash } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * NutritionCard — per-serving macro estimate.
 *
 * The LLM drafts one on recipe create (background task) and the owner can
 * tap "Re-estimate" after editing ingredients. The owner / co-editor / admin
 * can also edit any value by hand (switches `source` to `"manual"`).
 *
 * ALWAYS shows an "AI estimate only — not medical advice" footer when
 * `source === "ai"` so cooks don't rely on it for medication or strict
 * macro counts.
 */
const FIELDS = [
  { k: "kcal", label: "Calories", unit: "kcal", step: 10 },
  { k: "protein_g", label: "Protein", unit: "g" },
  { k: "carbs_g", label: "Carbs", unit: "g" },
  { k: "fat_g", label: "Fat", unit: "g" },
  { k: "fibre_g", label: "Fibre", unit: "g" },
  { k: "sugar_g", label: "Sugar", unit: "g" },
  { k: "sodium_mg", label: "Sodium", unit: "mg", step: 10 },
];

export default function NutritionCard({ recipe, canEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(recipe?.nutrition || {});

  const n = recipe?.nutrition;
  const hasData = n && typeof n.kcal === "number";
  // Tri-state opt-in: True = owner wants it, False = owner has hidden it,
  // null/undefined = legacy — show if data exists, prompt if it doesn't.
  const explicitShow = recipe?.show_nutrition;
  const effectiveShow = explicitShow === true || (explicitShow !== false && hasData);

  // Owner hid the card AND can't edit it back → render nothing.
  // Non-owners also see nothing when the owner has hidden it.
  if (explicitShow === false && !canEdit) return null;
  if (explicitShow === false) {
    // Owner view with the card hidden — show a thin "Show nutritional details"
    // affordance so they can flip it back on without diving into the edit page.
    return (
      <div
        className="rounded-2xl border border-dashed border-rf-ink/15 bg-rf-cream/40 px-5 py-3 my-2 flex items-center justify-between gap-3"
        data-testid="recipe-nutrition-hidden"
      >
        <div className="flex items-center gap-2 text-rf-ink-soft text-xs">
          <EyeSlash size={14} /> Nutritional details are hidden on this recipe.
        </div>
        <button
          type="button"
          onClick={async () => {
            setBusy(true);
            try {
              const { data } = await api.patch(`/recipes/${recipe.recipe_id}`, { show_nutrition: true });
              onChanged?.(data);
              toast.success("Nutritional details shown.");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not update");
            } finally { setBusy(false); }
          }}
          disabled={busy}
          data-testid="recipe-nutrition-show"
          className="text-xs font-bold text-rf-orange hover:underline disabled:opacity-50"
        >
          Show nutritional details
        </button>
      </div>
    );
  }

  // Nothing explicit AND no data AND viewer can't edit → render nothing.
  if (!effectiveShow && !canEdit) return null;

  const estimate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/recipes/${recipe.recipe_id}/estimate-nutrition`);
      if (data?.recipe) onChanged?.(data.recipe);
      toast.success("Nutrition estimated — remember it's an AI estimate, not medical advice.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nutrition estimate failed");
    } finally { setBusy(false); }
  };

  const beginEdit = () => {
    setDraft({ ...(recipe?.nutrition || {}) });
    setEditing(true);
  };

  const saveEdit = async () => {
    const body = {};
    for (const f of FIELDS) {
      if (draft[f.k] !== undefined && draft[f.k] !== "") body[f.k] = Number(draft[f.k]);
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/recipes/${recipe.recipe_id}/nutrition`, body);
      if (data?.recipe) onChanged?.(data.recipe);
      setEditing(false);
      toast.success("Nutrition updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  // Empty state — owner can edit, no data yet. Prompt to opt in.
  if (!hasData) {
    return (
      <div
        className="rounded-2xl border border-rf-ink/10 bg-white px-5 py-4 my-2 flex items-center justify-between gap-3"
        data-testid="recipe-nutrition-card"
      >
        <div className="flex items-center gap-2">
          <Pulse size={18} weight="duotone" className="text-rf-orange" />
          <p className="text-sm text-rf-ink-soft">Show AI-drafted nutritional details on this recipe?</p>
        </div>
        <button
          type="button"
          onClick={estimate}
          disabled={busy}
          data-testid="recipe-estimate-nutrition"
          className="btn-outline-ink !py-1.5 !px-3 text-xs disabled:opacity-50"
        >
          {busy ? "Working…" : "Add nutritional details"}
        </button>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border border-rf-ink/10 bg-white px-5 py-4 my-2"
      data-testid="recipe-nutrition-card"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Pulse size={18} weight="duotone" className="text-rf-orange" />
          <h3 className="font-display font-black text-rf-ink text-sm uppercase tracking-wider">Per serving</h3>
          <span className="text-[10px] text-rf-ink-soft">serves {n.serves}</span>
        </div>
        {canEdit && !editing && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={estimate}
              disabled={busy}
              data-testid="recipe-reestimate-nutrition"
              className="text-xs text-rf-ink-soft hover:text-rf-orange inline-flex items-center gap-1 px-2 py-1 rounded-md disabled:opacity-50"
              title="Re-estimate with AI"
            >
              <ArrowCounterClockwise size={14} />
              {busy ? "…" : "Re-estimate"}
            </button>
            <button
              type="button"
              onClick={beginEdit}
              data-testid="recipe-edit-nutrition"
              className="text-rf-ink-soft hover:text-rf-orange p-1"
              title="Edit manually"
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm("Hide nutritional details on this recipe? Your figures stay saved — you can show them again any time.")) return;
                setBusy(true);
                try {
                  const { data } = await api.patch(`/recipes/${recipe.recipe_id}`, { show_nutrition: false });
                  onChanged?.(data);
                  toast.success("Nutritional details hidden on this recipe.");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not update");
                } finally { setBusy(false); }
              }}
              disabled={busy}
              data-testid="recipe-nutrition-hide"
              className="text-rf-ink-soft hover:text-red-600 p-1 disabled:opacity-50"
              title="Hide on this recipe"
            >
              <EyeSlash size={14} />
            </button>
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setEditing(false)} className="text-rf-ink-soft hover:text-red-600 p-1" data-testid="recipe-nutrition-cancel">
              <X size={14} />
            </button>
            <button type="button" onClick={saveEdit} disabled={busy} className="text-rf-ink-soft hover:text-green-600 p-1 disabled:opacity-50" data-testid="recipe-nutrition-save">
              <Check size={16} weight="bold" />
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2.5">
        {FIELDS.map((f) => {
          const val = editing ? (draft[f.k] ?? "") : (n[f.k] ?? 0);
          return (
            <div key={f.k} className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-bold text-rf-ink-soft">{f.label}</div>
              {editing ? (
                <input
                  type="number"
                  step={f.step || 0.1}
                  value={val}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.k]: e.target.value }))}
                  data-testid={`recipe-nutrition-input-${f.k}`}
                  className="w-full text-sm font-bold text-rf-ink bg-rf-cream-alt rounded px-2 py-1 border border-rf-ink/10 focus:outline-none focus:border-rf-amber"
                />
              ) : (
                <div className="font-black text-rf-ink text-base" data-testid={`recipe-nutrition-${f.k}`}>
                  {typeof val === "number" ? val.toLocaleString() : val}
                  <span className="text-[10px] text-rf-ink-soft font-normal ml-0.5">{f.unit}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-start gap-1.5 mt-3 pt-3 border-t border-rf-ink/5" data-testid="recipe-nutrition-disclaimer">
        <Info size={12} className="text-rf-ink-soft/70 mt-0.5 flex-shrink-0" />
        <p className="text-[10px] text-rf-ink-soft leading-snug">
          {n.source === "manual"
            ? "Values set manually by the owner. For reference only — not medical advice. Always double-check if cooking for allergies, diabetes, or strict dietary needs."
            : "AI estimate only — not medical advice. Figures are approximations based on standard ingredient data and may vary by brand, portion size and preparation. Always double-check if cooking for allergies, diabetes, or strict dietary needs."}
        </p>
      </div>
    </div>
  );
}
